import { manufacturingFetch } from "@/lib/manufacturingClient";
import { resizeQcPhoto } from "@/lib/manufacturing/qcPhotoResize";
import { requireSupabase } from "@/lib/supabase";

const PATH = "/api/mfg/builder-assistant";

function post<T>(body: Record<string, unknown>) {
  return manufacturingFetch<T>(PATH, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export type QcChecklistCategory = {
  id: string;
  name: string;
  sort_index: number;
  status: string;
  required: boolean;
};

export type QcChecklistItem = {
  id: string;
  category_id: string;
  title: string;
  description: string;
  status: string;
  example_url: string | null;
  has_example?: boolean;
  source_observation_id: string | null;
  failure_photo_required: boolean;
  failure_note_required: boolean;
};

export type QcChecklistCheck = {
  item_id: string;
  result: string;
  note: string;
  observation_id: string | null;
  pass_url: string | null;
  has_pass?: boolean;
};

export type QcChecklistCatalog = {
  ok: true;
  categories: QcChecklistCategory[];
  items: QcChecklistItem[];
  is_owner: boolean;
  failure_photos_required: boolean;
  failure_notes_required: boolean;
};

let checklistCatalog: QcChecklistCatalog | null = null;
let checklistInflight: Promise<{ ok: true; data: QcChecklistCatalog } | { ok: false; error: string }> | null = null;

/** Last checklist the inspection tab loaded, so failure matching does not wait on the network. */
export function peekQcChecklist(): QcChecklistCatalog | null {
  return checklistCatalog;
}

export function forgetQcChecklist() {
  checklistCatalog = null;
}

function refreshQcChecklist() {
  if (!checklistInflight) {
    checklistInflight = post<QcChecklistCatalog>({ op: "checklist-list" })
      .then((res) => {
        if (res.ok) checklistCatalog = res.data;
        return res;
      })
      .finally(() => {
        checklistInflight = null;
      });
  }
  return checklistInflight;
}

export function listQcChecklist() {
  if (checklistCatalog) {
    void refreshQcChecklist();
    return Promise.resolve({ ok: true as const, data: checklistCatalog });
  }
  return refreshQcChecklist();
}

export type QcSessionSection = {
  category_id: string;
  submitted_at: string | null;
  result: "pass" | "fail" | null;
};

export function listQcSessionSections(sessionId: string) {
  return post<{ ok: true; category_ids: string[]; sections: QcSessionSection[] }>({
    op: "checklist-audit-sections",
    session_id: sessionId,
  });
}

export function submitQcSessionSection(sessionId: string, categoryId: string) {
  return post<{ ok: true; result: "pass" | "fail" }>({
    op: "submit-qc-section",
    session_id: sessionId,
    category_id: categoryId,
  });
}

export function setQcSessionSection(sessionId: string, categoryId: string, selected: boolean) {
  return post<{ ok: true }>({
    op: "checklist-audit-toggle",
    session_id: sessionId,
    category_id: categoryId,
    selected,
  });
}

export function setQcCategoryRequired(categoryId: string, required: boolean) {
  return post<{ ok: true }>({ op: "checklist-audit-required", category_id: categoryId, required });
}

export function setQcItemFailurePhoto(itemId: string, required: boolean) {
  forgetQcChecklist();
  return post<{ ok: true }>({ op: "checklist-audit-item-photo", item_id: itemId, required });
}

export type QcFailureEmailSettings = {
  enabled: boolean;
  from: string;
  subject: string;
  include_link: boolean;
  no_reply: boolean;
};

export function readQcFailureEmailSettings() {
  return post<{ ok: true; settings: QcFailureEmailSettings }>({ op: "checklist-email-settings" });
}

export function saveQcFailureEmailSettings(settings: QcFailureEmailSettings) {
  return post<{ ok: true; settings: QcFailureEmailSettings }>({
    op: "checklist-email-settings",
    enabled: settings.enabled,
    from: settings.from,
    subject: settings.subject,
    include_link: settings.include_link,
    no_reply: settings.no_reply,
  });
}

export type QcReportRow = {
  category: string;
  title: string;
  result: "pass" | "fail";
  note: string;
  photos: string[];
};

export function readQcReport(sessionId: string) {
  return post<{
    ok: true;
    report: { serial: string; plant: string; customer: string; model: string; signoff: string; rows: QcReportRow[] };
  }>({ op: "checklist-report", session_id: sessionId });
}

export function setQcOrgFailurePhotos(required: boolean) {
  return post<{ ok: true }>({ op: "checklist-audit-org-photo", required });
}

export function setQcOrgFailureNotes(required: boolean) {
  forgetQcChecklist();
  return post<{ ok: true }>({ op: "checklist-audit-org-notes", required });
}

export function listQcChecklistChecks(sessionId: string) {
  return post<{ ok: true; checks: QcChecklistCheck[] }>({ op: "checklist-checks", session_id: sessionId });
}

export function saveQcChecklistCategory(name: string) {
  forgetQcChecklist();
  return post<{ ok: true; id: string }>({ op: "checklist-save-category", name });
}

export function saveQcChecklistItem(input: {
  id?: string;
  category_id: string;
  title: string;
  description: string;
}) {
  forgetQcChecklist();
  return post<{ ok: true; id: string }>({ op: "checklist-save-item", ...input });
}

export function importQcChecklistCsv(csv: string) {
  forgetQcChecklist();
  return post<{ ok: true; imported: number }>({ op: "checklist-import", csv });
}

export function requestQcChecklistArchive(kind: "category" | "item", id: string) {
  forgetQcChecklist();
  return post<{ ok: true }>({ op: "checklist-request-archive", kind, id });
}

export function ownerQcChecklistDecision(input: {
  action: "finalize" | "archive" | "keep";
  kind: "category" | "item";
  id: string;
}) {
  forgetQcChecklist();
  return post<{ ok: true }>({ op: "checklist-owner", ...input });
}

export function promoteObservationToChecklist(observationId: string, categoryId: string) {
  forgetQcChecklist();
  return post<{ ok: true; id: string }>({
    op: "checklist-promote",
    observation_id: observationId,
    category_id: categoryId,
  });
}

export function recordQcChecklistChecks(
  sessionId: string,
  checks: { item_id: string; result: "pass" | "fail" | "na"; note?: string }[],
) {
  return post<{ ok: true; saved: { item_id: string; observation_id: string | null }[] }>({
    op: "checklist-check-batch",
    session_id: sessionId,
    checks,
  });
}

export function recordQcChecklistCheck(input: {
  session_id: string;
  item_id: string;
  result: "pass" | "fail" | "na";
  note?: string;
  observation_id?: string;
  photo_attached?: boolean;
}) {
  return post<{ ok: true; observation_id: string | null }>({ op: "checklist-check", ...input });
}

type SignOk = {
  ok: true;
  bucket: string;
  path: string;
  token: string;
  attachment_id: string;
  content_type: string;
};

export async function uploadQcChecklistExample(
  itemId: string,
  file: File,
): Promise<{ ok: true } | { ok: false; error: string }> {
  const prepared = await resizeQcPhoto(file);
  const contentType = (prepared.type || "image/jpeg").toLowerCase();
  const sign = await post<SignOk>({
    op: "checklist-sign-example",
    item_id: itemId,
    content_type: contentType,
    byte_size: prepared.size,
  });
  if (!sign.ok) return { ok: false, error: sign.error };
  const signed = sign.data;
  if (!signed?.path || !signed.token || !signed.bucket) {
    return { ok: false, error: "Example photo could not be signed." };
  }
  const { error: upErr } = await requireSupabase().storage.from(signed.bucket).uploadToSignedUrl(
    signed.path,
    signed.token,
    prepared,
    { contentType: signed.content_type || contentType, upsert: false },
  );
  if (upErr) return { ok: false, error: upErr.message };
  const commit = await post<{ ok: true }>({
    op: "checklist-commit-example",
    item_id: itemId,
    attachment_id: signed.attachment_id,
    object_path: signed.path,
    content_type: signed.content_type || contentType,
  });
  if (!commit.ok) return { ok: false, error: commit.error };
  return { ok: true };
}

async function uploadSigned(
  op: "checklist-sign-pass" | "checklist-sign-ocr",
  extra: Record<string, unknown>,
  file: File,
): Promise<{ ok: true; path: string; attachment_id: string; content_type: string } | { ok: false; error: string }> {
  const prepared = await resizeQcPhoto(file);
  const contentType = (prepared.type || "image/jpeg").toLowerCase();
  const sign = await post<SignOk>({
    op,
    ...extra,
    content_type: contentType,
    byte_size: prepared.size,
  });
  if (!sign.ok) return { ok: false, error: sign.error };
  const signed = sign.data;
  if (!signed?.path || !signed.token || !signed.bucket) {
    return { ok: false, error: "Photo could not be signed." };
  }
  const { error: upErr } = await requireSupabase().storage.from(signed.bucket).uploadToSignedUrl(
    signed.path,
    signed.token,
    prepared,
    { contentType: signed.content_type || contentType, upsert: false },
  );
  if (upErr) return { ok: false, error: upErr.message };
  return {
    ok: true,
    path: signed.path,
    attachment_id: signed.attachment_id,
    content_type: signed.content_type || contentType,
  };
}

export async function uploadQcChecklistPassPhoto(
  sessionId: string,
  itemId: string,
  file: File,
): Promise<{ ok: true } | { ok: false; error: string }> {
  const signed = await uploadSigned("checklist-sign-pass", { session_id: sessionId, item_id: itemId }, file);
  if (!signed.ok) return signed;
  const commit = await post<{ ok: true }>({
    op: "checklist-commit-pass",
    session_id: sessionId,
    item_id: itemId,
    object_path: signed.path,
  });
  if (!commit.ok) return { ok: false, error: commit.error };
  return { ok: true };
}

export async function readQcChecklistPhoto(file: File): Promise<{ ok: true; csv: string } | { ok: false; error: string }> {
  const signed = await uploadSigned("checklist-sign-ocr", {}, file);
  if (!signed.ok) return signed;
  const read = await post<{ ok: true; csv: string }>({
    op: "checklist-ocr",
    object_path: signed.path,
    content_type: signed.content_type,
  });
  if (!read.ok) return { ok: false, error: read.error };
  return { ok: true, csv: read.data.csv };
}
